import { TICK_MS } from "odyssey-shared";

/**
 * How the tooltips and the death screen print a quantity: a duration, a length in tiles, a signed percent and a list
 * of names. Pure text from numbers; no line of a tooltip is worded here.
 */

/**
 * A number to at most `places` decimals, with no trailing zeros.
 *
 * @param value The number.
 * @param places The most decimals.
 * @returns The text.
 */
function trimmed(value: number, places: number): string {
  return String(Number(value.toFixed(places)));
}

/**
 * A duration in ticks as a tooltip prints it: seconds to a tenth under a minute, else whole minutes and any seconds.
 *
 * @param ticks The duration in ticks.
 * @returns The text.
 * @example
 * durationText(160);
 * // "8 s"
 * durationText(1800);
 * // "1 min 30 s"
 */
export function durationText(ticks: number): string {
  const ms = ticks * TICK_MS;
  if (ms < 60_000) {
    return `${trimmed(ms / 1000, 1)} s`;
  }
  const seconds = Math.round(ms / 1000);
  const rest = seconds % 60;
  return rest === 0 ? `${Math.floor(seconds / 60)} min` : `${Math.floor(seconds / 60)} min ${rest} s`;
}

/**
 * A length in sixteenths of a tile as a tooltip prints it, in tiles to a hundredth.
 *
 * @param sixteenths The length.
 * @returns The text.
 * @example
 * tilesText(24);
 * // "1.5 tiles"
 */
export function tilesText(sixteenths: number): string {
  const tiles = trimmed(sixteenths / 16, 2);
  return tiles === "1" ? "1 tile" : `${tiles} tiles`;
}

/**
 * A list of names joined for a sentence: "A", "A and B", "A, B and C".
 *
 * @param names The names.
 * @returns The text.
 */
export function listed(names: readonly string[]): string {
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] as string}`;
}

/**
 * A diminished control's length as the diminishing returns' line prints it: seconds to a hundredth with no trailing
 * zeros under a minute, so a quarter of a 1 s stun reads "0.25 s", else as `durationText` prints it.
 *
 * @param ticks The length in ticks.
 * @returns The text.
 */
export function stepText(ticks: number): string {
  const ms = ticks * TICK_MS;
  return ms < 60_000 ? `${trimmed(ms / 1000, 2)} s` : durationText(ticks);
}

/**
 * A signed whole percent: "+20%", "-25%".
 *
 * @param value The percent.
 * @returns The text.
 * @example
 * signedPercent(-25);
 * // "-25%"
 */
export function signedPercent(value: number): string {
  return `${value > 0 ? "+" : ""}${value}%`;
}
